import { FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TeamHeroMenuItem } from '../../../../../../interfaces/pagebuilder';
import validationBuilder from '../../../../../../static/validations';
import VyButton from '../../../../../common/VyButton';
import VyModal from '../../../../../common/VyModal';
import VyTextField from '../../../../../common/VyTextField';

interface NewItemsProps {
  onItemCreated: (props: TeamHeroMenuItem) => void;
  defaultValues?: TeamHeroMenuItem;
  withHasModalOption?: boolean;
  isModal?: boolean;
  isModalOpen?: boolean;
  itemsLength?: number;
  onCloseModal?: () => void;
}

const ItemFormContainer = ({
  isModal = false,
  open = false,
  onClose,
  children,
}: {
  isModal?: boolean;
  open?: boolean;
  onClose?: () => void;
  children?: ReactNode;
}) => {
  if (isModal)
    return (
      <VyModal onClose={() => onClose?.()} open={open}>
        {() => <>{children}</>}
      </VyModal>
    );
  return <>{children}</>;
};

const ItemForm = ({ onItemCreated, defaultValues, withHasModalOption = true, isModal, isModalOpen, itemsLength, onCloseModal }: NewItemsProps) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({ defaultValues, mode: 'all' });
  const { t } = useTranslation(['pagebuilder', 'common']);
  const validation = validationBuilder(t);

  function handleItemCreated(props: TeamHeroMenuItem) {
    reset();
    onItemCreated(props);
    onCloseModal?.();
  }

  return (
    <ItemFormContainer isModal={isModal} open={isModalOpen} onClose={onCloseModal}>
      <form onSubmit={handleSubmit(handleItemCreated)} className="flex flex-col">
        <div className="flex flex-col p-4 overflow-scroll border-1">
          <div className="flex flex-col mt-4 space-y-2" style={{ maxHeight: isModal && defaultValues ? '60vh' : '100%' }}>
            <Controller control={control} name={`text`} render={({ field }) => <VyTextField {...field} label={t('pagebuilder:entry-title')} fullWidth />} />
            <Controller
              control={control}
              name={`url`}
              rules={validation('urlWithoutProtocol')}
              render={({ field }) => <VyTextField {...field} label="URL" fullWidth />}
            />
            <Controller
              control={control}
              name="openInNewTab"
              defaultValue={false}
              render={({ field: { onChange, value } }) => {
                return (
                  <FormControlLabel
                    className="w-full"
                    control={
                      <Switch
                        onChange={(e: any) => {
                          onChange(e.target.checked);
                        }}
                        checked={value}
                      />
                    }
                    label={t('pagebuilder:new-tab')}
                  />
                );
              }}
            />
            <input type="number" {...register('sortIndex', { valueAsNumber: true })} value={defaultValues?.sortIndex ?? itemsLength} className="hidden" />
            {!(isModal && defaultValues) && (
              <>
                <VyButton style={{ marginTop: '1rem' }} type="submit">
                  {defaultValues ? t('pagebuilder:save-changes') : t('pagebuilder:add-new-entry')}
                </VyButton>
                {errors && Object.values(errors).map((error) => error && <p key={error.message}>{error.message}</p>)}
              </>
            )}
          </div>
        </div>

        {isModal && defaultValues && (
          <>
            <VyButton style={{ marginTop: '1rem' }} type="submit">
              {t('pagebuilder:save-changes')}
            </VyButton>
            {errors && Object.values(errors).map((error) => error && <p key={error.message}>{error.message}</p>)}
          </>
        )}
      </form>
    </ItemFormContainer>
  );
};

export default ItemForm;
